import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  TablePagination,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import apiService from '../services/api';

// Helper to convert ISO datetime string to datetime-local format
function convertToDateTimeLocal(isoString: string | null | undefined): string {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    const offset = date.getTimezoneOffset();
    const local = new Date(date.getTime() - offset * 60 * 1000);
    return local.toISOString().slice(0, 16);
  } catch {
    return '';
  }
}

// Helper to convert datetime-local format back to ISO format
function convertFromDateTimeLocal(dateTimeLocal: string | null | undefined): string {
  if (!dateTimeLocal || dateTimeLocal.trim() === '') return new Date().toISOString();
  try {
    // Ensure the datetime string has seconds if missing
    let normalizedDateTime = dateTimeLocal;
    if (normalizedDateTime.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)) {
      // Add seconds if missing (format: YYYY-MM-DDTHH:mm)
      normalizedDateTime += ':00';
    }
    
    // Create a Date object from the datetime-local string (which is in local timezone)
    const date = new Date(normalizedDateTime);
    if (isNaN(date.getTime())) return new Date().toISOString();
    // Return as ISO string (with timezone info)
    return date.toISOString();
  } catch {
    return new Date().toISOString();
  }
}

interface Specimen {
  id: number;
  accession_number: string;
  patient_name: string;
  patient_dob: string;
  type: string;
  source: string;
  status: string;
  collected_at: string;
  received_at: string;
  created_by: number;
  created_at: string;
}

interface PaginatedSpecimens {
  specimens: Specimen[];
  total: number;
  page: number;
  pageSize: number;
}

const Specimens: React.FC = () => {
  const [specimens, setSpecimens] = useState<Specimen[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSpecimen, setEditingSpecimen] = useState<Specimen | null>(null);
  const [form, setForm] = useState<Partial<Specimen>>({});

  const fetchSpecimens = async (pageNum = 0, pageSize = 10) => {
    try {
      const data: PaginatedSpecimens = await apiService.getSpecimens(pageNum + 1, pageSize);
      setSpecimens(data.specimens || []);
      setTotal(data.total || 0);
    } catch (e) {
      setSpecimens([]);
      setTotal(0);
    }
  };

  useEffect(() => {
    fetchSpecimens(page, rowsPerPage);
    // eslint-disable-next-line
  }, [page, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openAddDialog = () => {
    setEditingSpecimen(null);
    setForm({});
    setOpenDialog(true);
  };

  const openEditDialog = (specimen: Specimen) => {
    setEditingSpecimen(specimen);
    setForm({
      ...specimen,
      collected_at: convertToDateTimeLocal(specimen.collected_at),
      received_at: convertToDateTimeLocal(specimen.received_at)
    });
    setOpenDialog(true);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Delete this specimen?')) {
      await apiService.deleteSpecimen(id.toString());
      fetchSpecimens(page, rowsPerPage);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingSpecimen(null);
  };

  const handleDialogSave = async () => {
    const specimenPayload = {
      ...form,
      collected_at: convertFromDateTimeLocal(form.collected_at),
      received_at: convertFromDateTimeLocal(form.received_at)
    };
    
    if (editingSpecimen) {
      // Edit
      await apiService.updateSpecimen(editingSpecimen.id.toString(), specimenPayload);
    } else {
      // Add
      await apiService.createSpecimen(specimenPayload);
    }
    setOpenDialog(false);
    setEditingSpecimen(null);
    setForm({});
    fetchSpecimens(page, rowsPerPage);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Button variant="contained" startIcon={<Add />} onClick={openAddDialog} sx={{ mb: 2 }}>
        Add Specimen
      </Button>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Accession #</TableCell>
              <TableCell>Patient Name</TableCell>
              <TableCell>DOB</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Collected At</TableCell>
              <TableCell>Received At</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {specimens.map((specimen) => (
              <TableRow key={specimen.id}>
                <TableCell>{specimen.id}</TableCell>
                <TableCell>{specimen.accession_number}</TableCell>
                <TableCell>{specimen.patient_name}</TableCell>
                <TableCell>{specimen.patient_dob}</TableCell>
                <TableCell>{specimen.type}</TableCell>
                <TableCell>{specimen.source}</TableCell>
                <TableCell>{specimen.status}</TableCell>
                <TableCell>{specimen.collected_at ? new Date(specimen.collected_at).toLocaleString() : ''}</TableCell>
                <TableCell>{specimen.received_at ? new Date(specimen.received_at).toLocaleString() : ''}</TableCell>
                <TableCell>{specimen.created_by}</TableCell>
                <TableCell>{new Date(specimen.created_at).toLocaleString()}</TableCell>
                <TableCell>
                  <IconButton onClick={() => openEditDialog(specimen)}><Edit /></IconButton>
                  <IconButton onClick={() => handleDelete(specimen.id)}><Delete /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
      <Dialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle>{editingSpecimen ? 'Edit Specimen' : 'Add Specimen'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Accession #"
            name="accession_number"
            type="text"
            fullWidth
            variant="standard"
            value={form.accession_number || ''}
            onChange={handleFormChange}
          />
          <TextField
            margin="dense"
            label="Patient Name"
            name="patient_name"
            type="text"
            fullWidth
            variant="standard"
            value={form.patient_name || ''}
            onChange={handleFormChange}
          />
          <TextField
            margin="dense"
            label="DOB"
            name="patient_dob"
            type="date"
            fullWidth
            variant="standard"
            value={form.patient_dob || ''}
            onChange={handleFormChange}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            margin="dense"
            label="Type"
            name="type"
            type="text"
            fullWidth
            variant="standard"
            value={form.type || ''}
            onChange={handleFormChange}
          />
          <TextField
            margin="dense"
            label="Source"
            name="source"
            type="text"
            fullWidth
            variant="standard"
            value={form.source || ''}
            onChange={handleFormChange}
          />
          <TextField
            margin="dense"
            label="Status"
            name="status"
            type="text"
            fullWidth
            variant="standard"
            value={form.status || ''}
            onChange={handleFormChange}
          />
          <TextField
            margin="dense"
            label="Collected At"
            name="collected_at"
            type="datetime-local"
            fullWidth
            variant="standard"
            value={form.collected_at || ''}
            onChange={handleFormChange}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            margin="dense"
            label="Received At"
            name="received_at"
            type="datetime-local"
            fullWidth
            variant="standard"
            value={form.received_at || ''}
            onChange={handleFormChange}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleDialogSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Specimens; 